import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Suspense, lazy, useEffect } from 'react';

import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import { LoadingSpinner } from './components/ui/index';
import { AdminAuthProvider } from './context/AdminAuthContext';
import AdminLayout from './components/admin/AdminLayout';
import RequireAdminAuth from './components/admin/RequireAdminAuth';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// ── Public pages ──────────────────────────────────────────────
const HomePage          = lazy(() => import('./pages/HomePage'));
const ServicesPage      = lazy(() => import('./pages/ServicesPage'));
const ServiceDetailPage = lazy(() => import('./pages/ServiceDetailPage'));  // NEW
const PricingPage       = lazy(() => import('./pages/PricingPage'));
const SamplesPage       = lazy(() => import('./pages/SamplesPage'));
const FreeLeadsPage     = lazy(() => import('./pages/FreeLeadsPage'));      // NEW
const ContactPage       = lazy(() => import('./pages/ContactPage'));        // UPDATED
const NotFoundPage      = lazy(() => import('./pages/NotFoundPage'));

// ── Admin pages ───────────────────────────────────────────────
const AdminLogin    = lazy(() => import('./pages/admin/AdminLogin'));
const AdminOverview = lazy(() => import('./pages/admin/AdminOverview'));
const AdminLeads    = lazy(() => import('./pages/admin/AdminLeads'));
const AdminPlans    = lazy(() => import('./pages/admin/AdminPlans'));
const AdminUpload   = lazy(() => import('./pages/admin/AdminUpload'));

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <LoadingSpinner />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
}

function PublicLayout({ children }) {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <div className="flex-1">{children}</div>
      <Footer />
    </div>
  );
}

function ProtectedAdminLayout() {
  return (
    <RequireAdminAuth>
      <AdminLayout />
    </RequireAdminAuth>
  );
}

export default function App() {
  return (
    <AdminAuthProvider>
      <Router>
        <ScrollToTop />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#ffffff',
              color: '#1e293b',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              fontSize: '14px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            },
            success: { iconTheme: { primary: '#22c55e', secondary: '#fff' } },
            error:   { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
          }}
        />
        <Suspense fallback={<PageLoader />}>
          <Routes>

            {/* ── Public routes ── */}
            <Route path="/"                element={<PublicLayout><HomePage /></PublicLayout>}          />
            <Route path="/services"        element={<PublicLayout><ServicesPage /></PublicLayout>}      />
            <Route path="/services/:slug"  element={<PublicLayout><ServiceDetailPage /></PublicLayout>} />
            <Route path="/pricing"         element={<PublicLayout><PricingPage /></PublicLayout>}       />
            <Route path="/samples"         element={<PublicLayout><SamplesPage /></PublicLayout>}       />
            <Route path="/free-leads"      element={<PublicLayout><FreeLeadsPage /></PublicLayout>}     />
            <Route path="/contact"         element={<PublicLayout><ContactPage /></PublicLayout>}       />
            <Route path="/privacy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
            <Route path="/terms" element={<PublicLayout><TermsOfService /></PublicLayout>} />

            {/* ── Admin login ── */}
            <Route path="/admin" element={<AdminLogin />} />

            {/* ── Admin dashboard (protected) ── */}
            <Route path="/admin" element={<ProtectedAdminLayout />}>
              <Route path="dashboard" element={<AdminOverview />} />
              <Route path="leads"     element={<AdminLeads />}    />
              <Route path="plans"     element={<AdminPlans />}    />
              <Route path="upload"    element={<AdminUpload />}   />
            </Route>

            {/* ── 404 ── */}
            <Route path="*" element={<PublicLayout><NotFoundPage /></PublicLayout>} />

          </Routes>
        </Suspense>
      </Router>
    </AdminAuthProvider>
  );
}
